"use strict";

/*
 * Purchase mode (Subscribe & save vs One-time) controller.
 *
 * - One global mode shared by the cart drawer toggle and the PDP toggle (sessionStorage).
 * - Any cart change triggers a sync: subscription mode applies the Appstle selling plan
 *   to every compatible item (type "breakfast reimagined"), one-time mode removes it.
 * - Single "Proceed to checkout" button syncs the cart first, then redirects to /checkout.
 * - Starter box toast on the collection page ("Added! N more to go").
 */
(function () {
  var MODE_KEY = 'grPurchaseMode';
  var CADENCE_KEY = 'grSellingPlanId'; /* box-level "Deliver every" plan */
  var UPSELL_KEY = 'grUpsellShown';
  var SUB = 'subscription';
  var ONE = 'one-time';
  var MEALS_GOAL = 15;
  /* Box-buildable product types (lowercase). 'whey' is the single merged
     type of the base-choice era (flavour × Rolled oats / Black barley
     variants); 'whey core' / 'whey heritage' cover carts created while the
     catalog still carried two lines; the legacy 'breakfast reimagined'
     keeps pre-bottle carts working. Must match box_types in
     snippets/gr-cart-drawer.liquid, snippets/gr-card-product-new.liquid and
     sections/gr-collection-product-grid.liquid. */
  var COMPATIBLE_TYPES = ['whey', 'whey core', 'whey heritage', 'breakfast reimagined'];
  var POUCH_PROTEIN_G = 15; /* claim since 2026-09-20 (was 25) */
  /* Fallback net weight per bottle. The real figure is per flavour and base
     (32–37 g) and comes from the card's data-base-*-net attributes — see
     netWeightFor(); this only covers a line whose card is not on the page. */
  var POUCH_WEIGHT_G = 35;

  /* Everything this script renders itself (drawer rows, toasts, checkout
     gating, bundle modal) reads snippets/gr-i18n-js.liquid — the same locale
     keys the Liquid markup uses, so an optimistic re-render can never show a
     different language than the first paint. The English literals passed
     as fallbacks only matter on a page that loaded without the snippet. */
  var I18N = (window.grI18n && window.grI18n.cart) || {};
  var LANG = String((window.grI18n && window.grI18n.lang) || document.documentElement.lang || 'en').split('-')[0];
  var ROOT = typeof window.grRoot === 'string' ? window.grRoot : '';

  /* Placeholders are %name% (Shopify's Liquid parser rejects braces inside
     an output tag, so the snippet cannot emit {name}). */
  function tr(key, fallback, vars) {
    var s = I18N[key] || fallback;
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        s = s.split('%' + k + '%').join(String(vars[k]));
      });
    }
    return s;
  }

  /* one / few / many for Ukrainian, one / other for English — mirrors
     snippets/gr-plural.liquid. */
  function grPlural(n, forms, fallback) {
    forms = forms || fallback || {};
    var form;
    if (LANG === 'uk' || LANG === 'ru') {
      var m10 = n % 10, m100 = n % 100;
      form = (m10 === 1 && m100 !== 11) ? 'one' : (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) ? 'few' : 'many';
    } else {
      form = n === 1 ? 'one' : 'other';
    }
    return forms[form] || forms.other || forms.many || '';
  }

  var syncing = false;
  var pendingSync = false;
  var syncTimer = null;
  var prevMeals = null;

  function getMode() {
    var mode;
    try {
      mode = sessionStorage.getItem(MODE_KEY) || SUB;
    } catch (e) {
      mode = SUB;
    }
    /* No selling plan anywhere in the catalog (e.g. products not yet added to
       the subscription app): subscription pricing cannot be honored at
       checkout, so everything must display and charge one-time prices. */
    if (mode === SUB && !getPlanId()) return ONE;
    return mode;
  }

  function setMode(mode) {
    try {
      sessionStorage.setItem(MODE_KEY, mode);
    } catch (e) {
      /* private mode — keep going with defaults */
    }
  }

  function modeRoot() {
    return document.querySelector('.gr-purchase-mode');
  }

  function cadenceOptions() {
    return Array.prototype.slice.call(
      document.querySelectorAll('input[name="gr-deliver-every"]')
    );
  }

  function getStoredCadence() {
    try {
      return sessionStorage.getItem(CADENCE_KEY) || null;
    } catch (e) {
      return null;
    }
  }

  function setStoredCadence(planId) {
    try {
      sessionStorage.setItem(CADENCE_KEY, String(planId));
    } catch (e) {
      /* private mode — keep going */
    }
  }

  function getPlanId() {
    /* Box-level cadence choice wins: stored value first (survives drawer
       re-renders), then whatever radio is checked in the drawer. Fall back
       to the theme default plan. */
    var options = cadenceOptions();
    var stored = getStoredCadence();
    if (stored) {
      if (!options.length) return parseInt(stored, 10) || stored;
      var match = options.filter(function (input) { return input.value === stored; })[0];
      if (match) return parseInt(stored, 10) || stored;
    }
    var checked = options.filter(function (input) { return input.checked; })[0];
    if (checked && checked.value) return parseInt(checked.value, 10) || checked.value;
    var root = modeRoot();
    if (root && root.dataset.planId) return parseInt(root.dataset.planId, 10) || root.dataset.planId;
    var pdp = document.querySelector('input[name^="gr-pdp-mode"][value]:not([value=""])');
    if (pdp) return parseInt(pdp.value, 10) || pdp.value;
    var bb = document.querySelector('[data-box-builder]');
    if (bb && bb.dataset.planId) return parseInt(bb.dataset.planId, 10) || bb.dataset.planId;
    return null;
  }

  function isCompatible(item) {
    return COMPATIBLE_TYPES.indexOf((item.product_type || '').toLowerCase()) !== -1;
  }

  function getDiscountPercent() {
    var root = modeRoot();
    if (root && root.dataset.discount) return parseFloat(root.dataset.discount) || 15;
    var bb = document.querySelector('[data-box-builder]');
    if (bb && bb.dataset.discount) return parseFloat(bb.dataset.discount) || 15;
    return 15;
  }

  function countMeals(cartData) {
    return (cartData.items || []).reduce(function (sum, item) {
      return isCompatible(item) ? sum + item.quantity : sum;
    }, 0);
  }

  /* ---------- UI ---------- */

  function updateCardPrices(mode) {
    document.querySelectorAll('[data-card-price]').forEach(function (el) {
      var price = mode === SUB && el.dataset.subPrice ? el.dataset.subPrice : el.dataset.onetimePrice;
      if (price && el.textContent !== price) el.textContent = price;
    });
  }

  document.addEventListener('gr:purchase-mode', function (event) {
    updateCardPrices(event.detail.mode);
    var summary = document.querySelector('[data-value-summary]');
    if (summary) summary.classList.toggle('gr-value-summary--sub', event.detail.mode === SUB);
  });

  function updateUI() {
    var mode = getMode();

    var root = modeRoot();
    if (root) {
      if (root.dataset.mode !== mode) root.dataset.mode = mode;
      var input = root.querySelector('input[name="gr-purchase-mode"][value="' + mode + '"]');
      if (input && !input.checked) input.checked = true;

      /* Re-check the remembered cadence after drawer re-renders (Liquid
         always marks the theme default plan as checked). */
      var storedCadence = getStoredCadence();
      if (storedCadence) {
        var cadInput = root.querySelector('input[name="gr-deliver-every"][value="' + storedCadence + '"]');
        if (cadInput && !cadInput.checked) cadInput.checked = true;
      }

      var footer = root.closest('.gr-cart-drawer-footer') || document;
      var current = footer.querySelector('[data-subtotal-current]');
      var compare = footer.querySelector('[data-subtotal-compare]');
      var samePrice = root.dataset.priceSub === root.dataset.priceOnetime;
      if (current) {
        var currentText = mode === SUB ? root.dataset.priceSub : root.dataset.priceOnetime;
        if (currentText && current.textContent !== currentText) current.textContent = currentText;
      }
      if (compare) {
        var compareDisplay = mode === SUB && !samePrice ? '' : 'none';
        if (compare.style.display !== compareDisplay) compare.style.display = compareDisplay;
      }
      /* Per-pouch average follows the active mode, same as the subtotal. */
      var unit = footer.querySelector('[data-subtotal-unit]');
      if (unit) {
        var unitText = mode === SUB ? root.dataset.unitSub : root.dataset.unitOnetime;
        setText(unit, unitText || '');
      }
      /* "15% off applied" note follows the same visibility rule as the
         compare price — hidden in one-time mode and when nothing in the
         cart is discountable (sub total == one-time total). */
      var tag = footer.querySelector('[data-discount-tag]');
      if (tag) {
        var tagDisplay = mode === SUB && !samePrice ? '' : 'none';
        if (tag.style.display !== tagDisplay) tag.style.display = tagDisplay;
      }
    }

    document.querySelectorAll('.gr-pdp-mode').forEach(function (pdpRoot) {
      if (pdpRoot.dataset.mode !== mode) pdpRoot.dataset.mode = mode;
      var value = mode === SUB ? 'subscription' : 'one-time';
      var pdpInput = pdpRoot.querySelector('input[data-mode-value="' + value + '"]');
      if (pdpInput && !pdpInput.checked) pdpInput.checked = true;

      var formId = pdpRoot.dataset.formId;
      var form = formId && document.getElementById(formId);
      var planInput = form && form.querySelector('input[name="selling_plan"]');
      if (planInput && planInput.disabled !== (mode !== SUB)) planInput.disabled = mode !== SUB;
    });

    /* Product cards react to the active purchase mode. */
    document.dispatchEvent(new CustomEvent('gr:purchase-mode', { detail: { mode: mode } }));
  }

  /* ---------- Cart sync ---------- */

  /* Every cart call carries the locale root ('' or '/uk'): the Ajax API
     answers in the language of the URL it is called on, and the drawer rows
     this script builds must read the same as the server-rendered ones. */
  function fetchCart() {
    return fetch(ROOT + '/cart.js', { headers: { 'Cache-Control': 'no-cache' } }).then(function (r) {
      return r.json();
    });
  }

  function cartPost(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) {
        /* Bail instead of returning an error payload — otherwise syncCart
           re-detects the same mismatched line and loops through all its
           iterations (e.g. a selling plan id the product doesn't offer). */
        return r.json().catch(function () { return {}; }).then(function (err) {
          throw new Error(err.description || err.message || (url + ' failed: ' + r.status));
        });
      }
      return r.json();
    });
  }

  function changeLine(key, quantity, sellingPlan) {
    return cartPost(ROOT + '/cart/change.js', {
      id: key,
      quantity: quantity,
      selling_plan: sellingPlan
    });
  }

  function findMismatch(cartData, mode, planId) {
    var wantId = parseInt(planId, 10) || planId;
    return (cartData.items || []).find(function (item) {
      if (!isCompatible(item)) return false;
      var alloc = item.selling_plan_allocation;
      if (mode === SUB) {
        if (!planId) return false;
        if (!alloc) return true;
        /* Cadence change: line subscribed to a different selling plan. */
        var haveId = alloc.selling_plan && alloc.selling_plan.id;
        return !!haveId && haveId !== wantId;
      }
      return !!alloc;
    });
  }

  /*
   * Applies/removes the selling plan line by line. Line keys shift when Shopify
   * merges lines, so we re-fetch the cart before every change instead of batching.
   */
  async function syncCart(mode, rerender) {
    if (syncing) {
      pendingSync = true;
      return;
    }
    syncing = true;
    var drawer = document.querySelector('cart-drawer');
    var changed = false;
    try {
      var planId = getPlanId();
      /* Fast exit: with no selling plan available anywhere AND no plan
         applied on the last known cart, nothing can be mismatched — skip
         the /cart.js round trip this sync would otherwise burn on every
         quantity change (it also eats into Shopify's cart rate budget). */
      var knownItems = (serverCart && serverCart.items) || [];
      var anySubscribed = knownItems.some(function (item) {
        return !!item.selling_plan_allocation;
      });
      var iterations = (!planId && serverCart && !anySubscribed) ? 0 : 25;
      for (var i = 0; i < iterations; i++) {
        /* QUANTITY AUTHORITY: never fight the optimistic quantity engine.
           changeLine sends an ABSOLUTE quantity read from a /cart.js fetch
           taken before the user's latest clicks — if it lands after the
           flush's /cart/update.js it silently reverts the quantity (user
           adds 2, cart snaps back to 1). While clicks are pending or a
           flush is in flight, defer the plan sync entirely; it reschedules
           itself once the queue is idle. */
        if (hasPendingQuantity() || quantityFlushing) {
          scheduleSync();
          break;
        }
        var cartData = await fetchCart();
        if (hasPendingQuantity() || quantityFlushing) {
          scheduleSync();
          break;
        }
        var item = findMismatch(cartData, mode, planId);
        if (!item) break;
        /* Background sync stays silent: no cart-loading overlay, it blocks
           clicks on the drawer steppers and reads as a frozen UI. */
        await changeLine(item.key, item.quantity, mode === SUB ? planId : null);
        changed = true;
      }
      /* No inline drawer re-render: totals update via JS immediately, the
         structural HTML refresh happens later through the idle-gated
         scheduleDrawerRerender (never mid-interaction). */
      if (changed && rerender) {
        refreshCartState();
        scheduleDrawerRerender();
      }
    } catch (e) {
      console.error('Purchase mode sync failed:', e);
    } finally {
      if (drawer) drawer.classList.remove('cart-loading');
      syncing = false;
      if (pendingSync) {
        pendingSync = false;
        syncCart(getMode(), rerender);
      }
    }
    return changed;
  }

  function scheduleSync() {
    clearTimeout(syncTimer);
    syncTimer = setTimeout(function () {
      syncCart(getMode(), true);
    }, 900);
  }

  /* ---------- Checkout ---------- */

  function waitForIdle(maxMs) {
    return new Promise(function (resolve) {
      var waited = 0;
      (function poll() {
        var busy = syncing || quantityFlushing || hasPendingQuantity();
        if (!busy || waited >= maxMs) return resolve();
        waited += 150;
        setTimeout(poll, 150);
      })();
    });
  }

  async function proceedToCheckout(button) {
    if (button.classList.contains('is-busy')) return;
    button.classList.add('is-busy');
    try {
      clearTimeout(syncTimer);
      /* Flush any queued optimistic quantity updates before checkout. */
      clearTimeout(quantityFlushTimer);
      flushQuantityQueue();
      await waitForIdle(8000);
      await syncCart(getMode(), false);
      window.location.href = ROOT + '/checkout';
    } finally {
      setTimeout(function () {
        button.classList.remove('is-busy');
      }, 3000);
    }
  }

  /* ---------- Starter box toast ---------- */

  function showToast(message) {
    var toast = document.querySelector('.gr-box-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'gr-box-toast';
      document.body.appendChild(toast);
    }
    toast.innerHTML = message;
    toast.classList.add('gr-box-toast--visible');
    clearTimeout(toast._hideTimer);
    toast._hideTimer = setTimeout(function () {
      toast.classList.remove('gr-box-toast--visible');
    }, 2600);
  }

  function maybeToast(meals) {
    if (!document.querySelector('.gr-section--collection-product-grid')) return;
    if (prevMeals === null || meals <= prevMeals) {
      prevMeals = meals;
      return;
    }
    var crossed = prevMeals < MEALS_GOAL && meals >= MEALS_GOAL;
    prevMeals = meals;
    if (crossed) {
      showToast(tr('progressComplete', 'Starter box complete. Free shipping included.'));
    } else if (meals < MEALS_GOAL) {
      showToast(tr('toastAdded', 'Added. <strong>%left% more</strong> to go.', { left: MEALS_GOAL - meals }));
    }
  }

  /* ---------- Box Builder visualizer (collection page) ---------- */

  var MILESTONES = [15, 20, 25, 30];

  function bbMessage(count) {
    if (count < 15) return tr('msgToStarter', 'Select <strong>%left% more</strong> meals to complete your Starter box.', { left: 15 - count });
    if (count < 20) return tr('msgTo20', 'Starter box complete. Add <strong>%left% more</strong> to reach the 20-pack.', { left: 20 - count });
    if (count < 25) return tr('msgTo25', '20-pack complete. Add <strong>%left% more</strong> to reach the 25-pack.', { left: 25 - count });
    if (count < 30) return tr('msgTo30', '25-pack complete. Add <strong>%left% more</strong> to reach the full 30-pack.', { left: 30 - count });
    return tr('msgFull', 'Full 30-pack complete \u2014 your box is fully packed.');
  }

  function bbImageUrl(url) {
    if (!url) return url;
    return url + (url.indexOf('?') === -1 ? '?width=160' : '&width=160');
  }

  function updateBoxBuilder(cartData) {
    var bb = document.querySelector('[data-box-builder]');
    if (!bb) return;

    /* /cart.js lists items newest-first — reverse so the first item added
       stays at slot 1 and new items fill towards the right. */
    var images = [];
    (cartData.items || []).slice().reverse().forEach(function (item) {
      if (!isCompatible(item)) return;
      for (var i = 0; i < item.quantity; i++) images.push(item.image);
    });
    var count = images.length;
    var prev = parseInt(bb.dataset.count || '0', 10);

    /* Cheap signature: if neither the count nor the slot images changed,
       skip the whole 30-slot walk — renders during a click burst then cost
       almost nothing. */
    var signature = count + '|' + images.join('|');
    if (bb._grSignature === signature) return;
    bb._grSignature = signature;

    if (bb.dataset.count !== String(count)) bb.dataset.count = count;
    bb.classList.toggle('gr-box-builder--unlocked', count >= MEALS_GOAL);
    /* Container border turns green only on an exact milestone (15/20/25/30). */
    bb.classList.toggle('gr-box-builder--milestone-hit', MILESTONES.indexOf(count) !== -1);

    var slots = bb.querySelectorAll('[data-bb-slot]');
    slots.forEach(function (slot, idx) {
      var filled = idx < count;
      slot.classList.toggle('is-filled', filled);
      var img = slot.querySelector('.gr-box-builder__img');
      if (filled && images[idx]) {
        var src = bbImageUrl(images[idx]);
        if (!img) {
          img = document.createElement('img');
          img.className = 'gr-box-builder__img';
          img.alt = '';
          slot.appendChild(img);
        }
        if (img.getAttribute('src') !== src) img.setAttribute('src', src);
      } else if (img) {
        img.remove();
      }
    });

    var message = bb.querySelector('[data-bb-message]');
    if (message) {
      var html = bbMessage(count);
      if (message._grHtml !== html) {
        message._grHtml = html;
        message.innerHTML = html;
      }
    }

    /* Milestone highlighting + pulse when a milestone is crossed. */
    MILESTONES.forEach(function (m) {
      var reached = count >= m;
      var marker = bb.querySelector('[data-bb-milestone="' + m + '"]');
      if (marker) marker.classList.toggle('is-reached', reached);
      var slot = slots[m - 1];

      if (prev < m && count >= m) {
        if (slot) {
          slot.classList.remove('gr-bb-pulse');
          void slot.offsetWidth;
          slot.classList.add('gr-bb-pulse');
        }
        bb.classList.remove('gr-bb-pulse');
        void bb.offsetWidth;
        bb.classList.add('gr-bb-pulse');
      }
    });
  }

  /* ---------- Single source of truth ----------
     Every successful cart AJAX response (or a fresh /cart.js fetch) flows
     through renderCartState, which reconciles all cart-dependent UI at once:
     quantity selectors on product cards, drawer inputs, drawer header
     (progress + title), the Box Builder visualizer and purchase-mode UI. */

  /* Programmatic value writes don't fire change events, so the +/- buttons'
     disabled state must be reconciled here — a stale ".disabled" class is
     what makes the minus button feel permanently frozen. */
  function syncStepperButtons(input, qty) {
    var wrap = input.closest('gr-quantity-selector') || input.parentElement;
    if (!wrap) return;
    /* Programmatic value writes bypass the element's own change handler,
       so the trash-at-1 glyph swap is reconciled here too. */
    wrap.classList.toggle('is-single', qty === 1);
    var minus = wrap.querySelector('.gr-quantity__button--minus');
    if (minus) minus.classList.toggle('disabled', qty <= 0);
    var plus = wrap.querySelector('.gr-quantity__button--plus');
    if (plus) plus.classList.remove('disabled');
  }

  function syncQuantityControls(cartData) {
    var byVariant = {};
    (cartData.items || []).forEach(function (item) {
      byVariant[item.variant_id] = (byVariant[item.variant_id] || 0) + item.quantity;
    });

    /* Attribute writes are guarded: setAttribute with an unchanged value
       still records a DOM mutation, which makes every third-party
       MutationObserver (Appstle etc.) re-run its callbacks on every
       optimistic frame — that main-thread noise is exactly what reads as
       micro-stutter during click bursts. */
    document.querySelectorAll('[data-quantity-variant-id]').forEach(function (input) {
      var qty = byVariant[input.getAttribute('data-quantity-variant-id')] || 0;
      if (input.getAttribute('data-cart-quantity') !== String(qty)) {
        input.setAttribute('data-cart-quantity', qty);
      }
      if (document.activeElement !== input && String(input.value) !== String(qty)) {
        input.value = qty;
      }
      syncStepperButtons(input, qty);
    });

    document.querySelectorAll('[data-card-variant-id]').forEach(function (input) {
      var qty = byVariant[input.getAttribute('data-card-variant-id')] || 0;
      if (input.getAttribute('data-gr-card-quantity') !== String(qty)) {
        input.setAttribute('data-gr-card-quantity', qty);
      }
      if (document.activeElement !== input && String(input.value) !== String(qty)) {
        input.value = qty;
      }
      syncStepperButtons(input, qty);
      var card = input.closest('.gr-products-grid__card');
      if (card) card.classList.toggle('gr-products-grid__card--in-cart', qty > 0);
    });
  }

  function syncDrawerHeader(cartData) {
    var meals = countMeals(cartData);

    /* Both drawer states (empty + full) carry their own header — update all. */
    document.querySelectorAll('.gr-box-progress').forEach(function (progress) {
      progress.classList.toggle('gr-box-progress--complete', meals >= MEALS_GOAL);
      var text = progress.querySelector('.gr-box-progress__text');
      if (text) {
        if (meals === 0) {
          setText(text, tr('progressEmpty', 'Add %goal% meals to complete your Starter box. Shipping is free.', { goal: MEALS_GOAL }));
        } else if (meals < MEALS_GOAL) {
          setText(text, tr('progressPartial', 'Added: %n%/%goal% meals. Add %left% more to complete your Starter box. Shipping is free.', { n: meals, goal: MEALS_GOAL, left: MEALS_GOAL - meals }));
        } else if (!text.querySelector('[data-bp-complete]')) {
          text.innerHTML = '<span data-bp-complete>' + tr('progressComplete', 'Starter box complete. Free shipping included.') + '</span>';
        }
      }
    });

    document.querySelectorAll('.gr-cart-drawer-title').forEach(function (title) {
      setText(title, meals > 0
        ? grPlural(meals, I18N.titleCount, { one: '%n% meal in box', other: '%n% meals in box' }).split('%n%').join(String(meals))
        : tr('titleEmpty', 'Your box'));
    });
  }

  /* ---------- Header cart bubble (milestone ring) ----------
     Mirrors the Liquid logic in gr-cart-btn / cart-icon-bubble: the ring
     fills towards the NEXT orderable box size (15/20/25/30) and closes
     fully — turning green with a pop — only on an exact milestone. */

  /* Meals per pouch from the product card's badge ("1 meal / pouch" -> 1);
     mirrors the gr_package_quantity metafield gr-cart-counter.liquid uses. */
  function packageUnitsFor(variantId) {
    var n = parseInt(packageBadgeFor(variantId), 10);
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function cartUnits(cartData) {
    return (cartData.items || []).reduce(function (sum, item) {
      return sum + packageUnitsFor(item.variant_id) * item.quantity;
    }, 0);
  }

  function nextMilestone(units) {
    if (units > 25) return 30;
    if (units > 20) return 25;
    if (units > 15) return 20;
    return 15;
  }

  var prevBubbleUnits = null;

  function syncHeaderBubble(cartData) {
    var bubble = document.querySelector('.gr-cart-btn__count-bubble');
    if (!bubble) return;
    var units = cartUnits(cartData);
    var next = nextMilestone(units);
    var onMilestone = MILESTONES.indexOf(units) !== -1;

    var numEl = bubble.querySelector('.gr-text-small');
    if (numEl) setText(numEl, String(units));
    window.cartCounter = units;

    bubble.style.setProperty('--pie-size', String(Math.min(units, next)));
    bubble.style.setProperty('--pie-sizes', String(next));
    bubble.classList.toggle('gr-cart-btn__count-bubble--full', onMilestone);
    if (onMilestone) {
      /* Restart the pop animation only when the milestone is newly reached. */
      if (prevBubbleUnits !== units) {
        bubble.classList.remove('gr-cart-btn__count-bubble--milestone');
        void bubble.offsetWidth;
        bubble.classList.add('gr-cart-btn__count-bubble--milestone');
      }
    } else {
      bubble.classList.remove('gr-cart-btn__count-bubble--milestone');
    }
    prevBubbleUnits = units;
  }

  /* ---------- Optimistic drawer line items ----------
     The drawer's line list is reconciled client-side against cart data on
     every render: a freshly added item gets its row built from local data
     THE MOMENT the user clicks +, and a removed item disappears right away —
     no waiting for a server-rendered section. The idle structural re-render
     later replaces everything with canonical server HTML. */

  function escapeHtml(value) {
    return String(value == null ? '' : value)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function drawerImageUrl(url) {
    if (!url) return url;
    return url + (url.indexOf('?') === -1 ? '?width=100' : '&width=100');
  }

  var DRAWER_MINUS_SVG = '<svg class="gr-icon gr-icon--minus" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M19 12.75H5C4.586 12.75 4.25 12.414 4.25 12C4.25 11.586 4.586 11.25 5 11.25H19C19.414 11.25 19.75 11.586 19.75 12C19.75 12.414 19.414 12.75 19 12.75Z" fill="currentColor"/></svg>';
  /* Same markup as snippets/gr-icon-trash.liquid — shown instead of the minus
     glyph when the line is at quantity 1 (.is-single on the selector). */
  var DRAWER_TRASH_SVG = '<svg class="gr-icon gr-icon--trash" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M21 5.25H17.441C16.54 5.25 16.502 5.136 16.255 4.396L16.053 3.789C15.746 2.869 14.889 2.25 13.919 2.25H10.081C9.11099 2.25 8.253 2.868 7.947 3.789L7.745 4.396C7.498 5.137 7.46 5.25 6.559 5.25H3C2.586 5.25 2.25 5.586 2.25 6C2.25 6.414 2.586 6.75 3 6.75H4.298L5.065 18.249C5.213 20.474 6.57701 21.75 8.80701 21.75H15.194C17.423 21.75 18.787 20.474 18.936 18.249L19.703 6.75H21C21.414 6.75 21.75 6.414 21.75 6C21.75 5.586 21.414 5.25 21 5.25ZM9.37 4.263C9.473 3.956 9.75799 3.75 10.081 3.75H13.919C14.242 3.75 14.528 3.956 14.63 4.263L14.832 4.87C14.876 5.001 14.92 5.128 14.968 5.25H9.03C9.078 5.127 9.12301 5 9.16701 4.87L9.37 4.263ZM17.438 18.149C17.343 19.582 16.629 20.25 15.193 20.25H8.806C7.37 20.25 6.657 19.583 6.561 18.149L5.801 6.75H6.558C6.683 6.75 6.787 6.737 6.899 6.729C6.933 6.734 6.964 6.75 6.999 6.75H16.999C17.035 6.75 17.065 6.734 17.099 6.729C17.211 6.737 17.315 6.75 17.44 6.75H18.197L17.438 18.149ZM14.75 11V16C14.75 16.414 14.414 16.75 14 16.75C13.586 16.75 13.25 16.414 13.25 16V11C13.25 10.586 13.586 10.25 14 10.25C14.414 10.25 14.75 10.586 14.75 11ZM10.75 11V16C10.75 16.414 10.414 16.75 10 16.75C9.586 16.75 9.25 16.414 9.25 16V11C9.25 10.586 9.586 10.25 10 10.25C10.414 10.25 10.75 10.586 10.75 11Z" fill="currentColor"/></svg>';
  var DRAWER_PLUS_SVG = '<svg class="gr-icon gr-icon--plus" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"><path d="M19.75 12C19.75 12.414 19.414 12.75 19 12.75H12.75V19C12.75 19.414 12.414 19.75 12 19.75C11.586 19.75 11.25 19.414 11.25 19V12.75H5C4.586 12.75 4.25 12.414 4.25 12C4.25 11.586 4.586 11.25 5 11.25H11.25V5C11.25 4.586 11.586 4.25 12 4.25C12.414 4.25 12.75 4.586 12.75 5V11.25H19C19.414 11.25 19.75 11.586 19.75 12Z" fill="currentColor"/></svg>';

  /* Product card element that carries the data-cart-* attributes for a
     variant — the source for building drawer rows without the server.
     A base-choice card serves TWO variants but data-card-variant-id only
     points at the currently selected one — fall back to the card's full
     comma-delimited variant list so the other base still resolves. */
  function cardForVariant(variantId) {
    var input = document.querySelector('[data-card-variant-id="' + variantId + '"]');
    if (input) return cardElFor(input);
    return document.querySelector('[data-variant-ids*=",' + variantId + ',"]');
  }

  /* "1 meal / pouch" badge text comes from a product metafield that cart
     AJAX data doesn't expose — read it from the product card's data
     attribute so the optimistic row matches the Liquid render exactly. */
  function packageBadgeFor(variantId) {
    var card = cardForVariant(variantId);
    return (card && card.getAttribute('data-cart-badge')) || '';
  }

  function drawerRowTemplate(item) {
    var li = document.createElement('li');
    li.className = 'gr-drawer-list__item';
    /* Prefer the card's data-cart-image: it is the same relative width-100
       URL the Liquid drawer renders (and it's preloaded), while /cart.js
       returns an absolute cdn.shopify.com URL — a different cache entry
       that would reload visibly when the server HTML lands. */
    var card = cardForVariant(item.variant_id);
    var imageSrc = (card && card.getAttribute('data-cart-image')) || drawerImageUrl(item.image);
    var image = imageSrc
      ? '<img class="gr-cart-item__image" src="' + escapeHtml(imageSrc) + '" alt="" width="100" height="100">'
      : '';
    var badgeText = packageBadgeFor(item.variant_id);
    var badge = badgeText
      ? '<span class="gr-cart-item__badge gr-drawer-badge gr-drawer-badge--default gr-text-small">' + escapeHtml(badgeText) + '</span>'
      : '';
    /* Second line: the chosen base (variant title) when the item has one —
       two bases of the same flavour must never look identical in the
       drawer. Legacy single-variant items fall back to the product type.
       Must stay in lockstep with the Liquid row in gr-cart-drawer.liquid. */
    var baseLine = item.variant_title && item.variant_title !== 'Default Title'
      ? item.variant_title
      : item.product_type;
    li.innerHTML =
      '<div class="gr-cart-item">' + image +
        '<div class="gr-cart-item__details">' +
          '<div class="gr-cart-item__title">' + escapeHtml(item.product_title || item.title) + '</div>' +
          '<div class="gr-cart-item__type gr-text-small gr-text-capitalize gr-text-secondary">' + escapeHtml(baseLine) + '</div>' +
          badge +
          '<div class="gr-quantity-selector-wrap gr-cart-item__quantity gr-quantity-selector--small">' +
            '<gr-quantity-selector class="gr-quantity-selector' + (parseInt(item.quantity, 10) === 1 ? ' is-single' : '') + '">' +
              '<button class="gr-button gr-button--flat gr-button--icon no-js-hidden gr-quantity__button gr-quantity__button--minus" type="button" name="minus">' +
                DRAWER_MINUS_SVG + DRAWER_TRASH_SVG + '<span class="visually-hidden">' + escapeHtml(tr('decrease', 'Decrease quantity')) + '</span>' +
              '</button>' +
              '<input class="gr-quantity-selector__input" type="text" size="1" min="0" inputmode="numeric" name="updates[]" autocomplete="off" pattern="\\d*"' +
                ' value="' + parseInt(item.quantity, 10) + '"' +
                ' data-quantity-variant-id="' + escapeHtml(item.variant_id) + '"' +
                ' data-cart-quantity="' + parseInt(item.quantity, 10) + '">' +
              '<button class="gr-button gr-button--flat gr-button--icon no-js-hidden gr-quantity__button gr-quantity__button--plus" type="button" name="plus">' +
                DRAWER_PLUS_SVG + '<span class="visually-hidden">' + escapeHtml(tr('increase', 'Increase quantity')) + '</span>' +
              '</button>' +
            '</gr-quantity-selector>' +
          '</div>' +
        '</div>' +
      '</div>';
    return li;
  }

  function syncDrawerItems(cartData) {
    var list = document.querySelector('cart-drawer .gr-drawer-list');
    if (!list) return;

    var items = (cartData.items || []).filter(function (item) {
      return item.quantity > 0;
    });
    var byVariant = {};
    items.forEach(function (item) {
      var vid = String(item.variant_id);
      if (!byVariant[vid]) byVariant[vid] = item;
    });

    /* Drop (or fade, while the user is mid-burst on the drawer steppers)
       rows whose variant is gone; remember which variants have a row. */
    var rows = {};
    Array.prototype.slice.call(list.querySelectorAll('.gr-drawer-list__item')).forEach(function (row) {
      var input = row.querySelector('input[data-quantity-variant-id]');
      if (!input) return; /* gift/promo rows — not ours to manage */
      var vid = input.getAttribute('data-quantity-variant-id');
      if (!byVariant[vid] || rows[vid]) {
        if (isInteractingWithDrawer()) {
          row.classList.add('gr-drawer-list__item--removing');
        } else {
          row.remove();
        }
      } else {
        row.classList.remove('gr-drawer-list__item--removing');
        rows[vid] = row;
      }
    });

    /* Build rows for items that have none yet, keeping cart order
       (newest first, same as the Liquid render). */
    var prevRow = null;
    items.forEach(function (item) {
      var vid = String(item.variant_id);
      var row = rows[vid];
      if (!row) {
        row = drawerRowTemplate(item);
        list.insertBefore(row, prevRow ? prevRow.nextSibling : list.firstChild);
        rows[vid] = row;
      }
      prevRow = row;
    });
  }

  /* ---------- Dynamic Value Summary (collection page) ---------- */

  /* Prices we print ourselves take the format of the buyer's market — the one
     Liquid's `money` filter uses for this session (Shopify.money_format in
     both layouts: "${{amount}}" in the US market, "₴{{amount}}" in the
     Ukrainian one). It has to be the very string Liquid prints, thousands
     separator included: the cards compare our text with the server's, and
     the optimistic drawer would flicker between "₴2575.95" and "₴2,575.95".
     A hard-coded "$" here put a dollar sign in front of hryvnia amounts.
     The plain-dollar fallback only runs if the layout's formatter is absent. */
  function formatMoney(cents) {
    var value = Math.round(cents);
    var shopify = window.Shopify;
    if (shopify && typeof shopify.formatMoney === 'function' && shopify.money_format) {
      try {
        return shopify.formatMoney(value, shopify.money_format);
      } catch (e) { /* an unknown placeholder in the format — fall through */ }
    }
    return '$' + (value / 100).toFixed(2);
  }

  function updateValueSummary(cartData) {
    var summary = document.querySelector('[data-value-summary]');
    if (!summary) return;

    var count = countMeals(cartData);
    summary.classList.toggle('is-empty', count === 0);

    var discount = getDiscountPercent();

    /* Savings = exact difference between one-time and subscription unit
       prices (allocation truth when a plan is applied), never a percentage
       of an approximate total. */
    var savingsCents = 0;
    (cartData.items || []).forEach(function (item) {
      if (!isCompatible(item)) return;
      savingsCents += (itemBasePrice(item) - itemSubUnitPrice(item, discount)) * item.quantity;
    });

    var set = function (selector, value) {
      var el = summary.querySelector(selector);
      if (el && el.textContent !== String(value)) el.textContent = value;
    };
    /* Net weight: per bottle from the variant (the card carries both bases),
       mirroring the Liquid sum in gr-collection-product-grid. */
    var weightG = 0;
    (cartData.items || []).forEach(function (item) {
      if (!isCompatible(item)) return;
      weightG += item.quantity * netWeightFor(item.variant_id);
    });

    set('[data-vs-count]', count);
    set('[data-vs-count-word]', grPlural(count, I18N.meals, { one: 'meal', other: 'meals' }));
    set('[data-vs-protein]', count * POUCH_PROTEIN_G);
    set('[data-vs-weight]', Math.round(weightG));
    set('[data-vs-days]', count);
    set('[data-vs-days-word]', grPlural(count, I18N.days, { one: 'day', other: 'days' }));
    set('[data-vs-savings]', formatMoney(savingsCents));
  }

  /* Drawer subtotal + purchase-mode prices, computed in JS from cart data —
     the drawer HTML is no longer re-rendered on quantity changes, so these
     numbers must not depend on server-rendered markup. */

  /* ---------- Unit price lock ----------
     Shopify can return the SAME line with prices from different market
     contexts depending on which endpoint produced the response (observed
     on this store: base $3.75/plan $3.19 vs base $4.24/plan $3.60).
     Recomputing totals from every response therefore makes the subtotal
     visibly dip and bounce even when the user changed nothing.
     The fix: the first unit price seen for a variant is locked for the
     lifetime of the page, and every total is quantity × locked price —
     totals can then only ever change when quantities change. */
  var lockedUnitPrices = {}; /* variantId -> { base, sub } in cents */

  function unitPricesFor(item, discount) {
    var vid = String(item.variant_id);
    if (lockedUnitPrices[vid]) return lockedUnitPrices[vid];
    var alloc = item.selling_plan_allocation;
    var base = alloc && alloc.compare_at_price ? alloc.compare_at_price : (item.original_price || item.price);
    var sub;
    if (alloc && typeof alloc.price === 'number') {
      sub = alloc.price;
    } else if (!isCompatible(item)) {
      sub = base;
    } else {
      sub = Math.round(base * (100 - discount) / 100);
    }
    /* Only authoritative /cart.js items may lock. An optimistic stub built
       from the product card can carry a page-cached (wrong-market) price —
       locking it would freeze a wrong number for the whole session. The stub
       still RETURNS its best guess for the instant render; the /cart.js
       refetch that follows every mutation sets the permanent lock. */
    if (!base || item._gr_stub) return { base: base || 0, sub: sub || 0 };
    lockedUnitPrices[vid] = { base: base, sub: sub };
    return lockedUnitPrices[vid];
  }

  function itemBasePrice(item) {
    return unitPricesFor(item, getDiscountPercent()).base;
  }

  function itemSubUnitPrice(item, discount) {
    return unitPricesFor(item, discount).sub;
  }

  function setText(el, value) {
    if (el && el.textContent !== value) el.textContent = value;
  }

  function syncDrawerTotals(cartData) {
    var discount = getDiscountPercent();

    /* Reveal/hide the Subscribe & save toggle from the live meal count —
       the markup is always in the DOM (Liquid renders it with a catalog
       fallback plan even for an empty cart), so the first optimistic add
       shows the full footer instantly. */
    var meals = countMeals(cartData);
    var toggleRoot = modeRoot();
    if (toggleRoot) {
      toggleRoot.classList.toggle('gr-purchase-mode--hidden', meals === 0);
    }

    var oneTimeCents = 0;
    var subCents = 0;
    /* Meals-only sums drive the average per-pouch price (accessories like
       the shaker are excluded from both the money and the pouch count). */
    var mealsOneCents = 0;
    var mealsSubCents = 0;
    (cartData.items || []).forEach(function (item) {
      var baseLine = itemBasePrice(item) * item.quantity;
      var subLine = itemSubUnitPrice(item, discount) * item.quantity;
      oneTimeCents += baseLine;
      subCents += subLine;
      if (isCompatible(item)) {
        mealsOneCents += baseLine;
        mealsSubCents += subLine;
      }
    });

    var subText = formatMoney(subCents);
    var onetimeText = formatMoney(oneTimeCents);
    var unitSubText = meals > 0 ? tr('unit', '%price% / serving', { price: formatMoney(Math.round(mealsSubCents / meals)) }) : '';
    var unitOnetimeText = meals > 0 ? tr('unit', '%price% / serving', { price: formatMoney(Math.round(mealsOneCents / meals)) }) : '';

    var root = modeRoot();
    if (root) {
      if (root.dataset.priceSub !== subText) root.dataset.priceSub = subText;
      if (root.dataset.priceOnetime !== onetimeText) root.dataset.priceOnetime = onetimeText;
      if (root.dataset.unitSub !== unitSubText) root.dataset.unitSub = unitSubText;
      if (root.dataset.unitOnetime !== unitOnetimeText) root.dataset.unitOnetime = unitOnetimeText;

      var options = root.querySelectorAll('.gr-purchase-mode__price');
      setText(options[0], subText);
      setText(options[1], onetimeText);
      setText(root.querySelector('[data-unit-price-sub]'), unitSubText || '');
      setText(root.querySelector('[data-unit-price-onetime]'), unitOnetimeText || '');

      var footer = root.closest('.gr-cart-drawer-footer') || document;
      setText(footer.querySelector('[data-subtotal-compare]'), onetimeText);
      /* [data-subtotal-current] is written by updateUI from the dataset. */

      /* The upsell popup's per-button price counters are Liquid-rendered —
         keep them in lockstep with the JS-computed totals. */
      var popup = document.querySelector('.gr-drawer-popup');
      if (popup) {
        popup.querySelectorAll('.gr-cart-drawer-footer__button-wrap').forEach(function (wrap) {
          var btn = wrap.querySelector('.gr-purchase-checkout');
          var counter = wrap.querySelector('.gr-button-unlock-counter');
          if (!btn || !counter) return;
          setText(counter, btn.dataset.setMode === SUB ? subText : onetimeText);
        });
      }
    } else {
      /* No purchase-mode toggle rendered yet (drawer server-rendered while
         the cart was empty) — write the subtotal directly so the very first
         optimistic add shows correct numbers. */
      var current = getMode() === SUB ? subText : onetimeText;
      document.querySelectorAll('.gr-cart-subtotal [data-subtotal-current]').forEach(function (el) {
        setText(el, current);
      });
      var currentUnit = getMode() === SUB ? unitSubText : unitOnetimeText;
      document.querySelectorAll('.gr-cart-subtotal [data-subtotal-unit]').forEach(function (el) {
        setText(el, currentUnit || '');
      });
    }

    /* Checkout gating: a box is orderable only at exactly 15/20/25/30 meals.
       Off-milestone the button is disabled and tells the customer what to
       add, with a live "N/goal" counter (mirrors the Liquid render). */
    var ready = MILESTONES.indexOf(meals) !== -1;
    var goal = nextMilestone(meals);
    var label;
    if (ready) {
      label = tr('checkout', 'Proceed to checkout');
    } else if (meals < 15) {
      label = tr('minMeals', 'Please select at least 15 meals');
    } else if (meals > 30) {
      label = tr('maxMeals', 'Maximum box is 30 meals');
    } else {
      label = tr('chooseSize', 'Choose 15, 20, 25 or 30 meals');
    }
    document.querySelectorAll('.gr-purchase-checkout--main').forEach(function (btn) {
      var labelEl = btn.querySelector('[data-checkout-label]');
      if (labelEl) {
        setText(labelEl, label);
      } else if (btn.textContent.trim() !== label) {
        btn.textContent = label;
      }
      if (btn.disabled !== !ready) btn.disabled = !ready;
      btn.classList.toggle('disabled', !ready);
      var wrap = btn.closest('.gr-cart-drawer-footer__button-wrap');
      /* No counter pill on a ready box — drop the reserved right padding so
         the "Proceed to checkout" label is optically centered. */
      if (wrap) wrap.classList.toggle('gr-cart-drawer-footer__button-wrap--ready', ready);
      var counter = wrap && wrap.querySelector('[data-checkout-counter]');
      if (counter) {
        setText(counter, meals + '/' + goal);
        var counterDisplay = ready ? 'none' : '';
        if (counter.style.display !== counterDisplay) counter.style.display = counterDisplay;
      }
    });

    /* Mobile drawer opener mirrors the same gating in real time: gray
       "locked" look + adaptive label + N/goal pill off-milestone, green
       review CTA with the box count on a milestone. It updates on every
       optimistic render, so the counter never lags a click. */
    var opener = document.getElementById('CartMobOpener');
    if (opener) {
      var openerBtn = opener.querySelector('.grJS-cart-opener');
      if (openerBtn) {
        openerBtn.classList.toggle('gr-mob-opener--locked', !ready);
        openerBtn.classList.remove('disabled');
        if (openerBtn.disabled) openerBtn.disabled = false;
        var openerLabel = opener.querySelector('[data-mob-opener-label]') ||
          openerBtn.querySelector('.gr-button__text');
        if (openerLabel) {
          setText(openerLabel, ready ? tr('reviewBox', 'Review box or keep adding') : label);
        }
      }
      var openerCounter = opener.querySelector('.gr-button-unlock-counter');
      if (openerCounter) {
        setText(openerCounter, ready ? String(meals) : meals + '/' + goal);
      }
    }

    /* The hidden form submit keeps the simple has-items rule (it is only
       triggered programmatically by legacy flows). */
    var hasItems = (cartData.items || []).some(function (item) { return item.quantity > 0; });
    document.querySelectorAll('.gr-cart-drawer__submit').forEach(function (btn) {
      if (btn.disabled !== !hasItems) btn.disabled = !hasItems;
      btn.classList.toggle('disabled', !hasItems);
    });
  }

  function renderCartState(cartData) {
    window.cart = cartData;
    /* The empty state is only ever shown when the cart truly has 0 items —
       never as a transient state while a request is in flight. Flipping TO
       empty is deferred while the user is working the drawer steppers, so
       the buttons never disappear under their cursor mid-burst. */
    var drawerEl = document.querySelector('cart-drawer');
    if (drawerEl) {
      var hasItems = (cartData.items || []).length > 0;
      if (hasItems) {
        drawerEl.classList.remove('is-empty');
      } else if (!isInteractingWithDrawer()) {
        drawerEl.classList.add('is-empty');
      }
    }
    syncDrawerItems(cartData);
    syncQuantityControls(cartData);
    syncDrawerHeader(cartData);
    syncHeaderBubble(cartData);
    updateBoxBuilder(cartData);
    updateValueSummary(cartData);
    syncDrawerTotals(cartData);
    maybeToast(countMeals(cartData));
    updateUI();
    document.dispatchEvent(new CustomEvent('gr:cart:render', { detail: { cart: cartData } }));
  }

  /* Token guard: if several refreshes overlap, only the newest one renders. */
  var renderToken = 0;

  function refreshCartState(cartData) {
    /* While optimistic updates are pending/in flight, a plain refetch would
       render stale server state on top of what the user just clicked. */
    if (!cartData && (hasPendingQuantity() || quantityFlushing)) return Promise.resolve();
    var token = ++renderToken;
    var promise = cartData && cartData.items ? Promise.resolve(cartData) : fetchCart();
    return promise.then(function (data) {
      if (token !== renderToken) return data;
      serverCart = data;
      renderCartState(data);
      return data;
    }).catch(function () {});
  }

  window.grRefreshCartState = refreshCartState;

  /* Desync fix: intercept every cart AJAX call (add/change/update/clear),
     no matter which component fired it (drawer steppers, product cards,
     PDP form, bundles), and pipe the successful response into the same
     global render. The token guard keeps out-of-order responses away. */
  (function interceptCartFetch() {
    var origFetch = window.fetch;
    window.fetch = function () {
      var url = typeof arguments[0] === 'string' ? arguments[0] : (arguments[0] && arguments[0].url) || '';
      var result = origFetch.apply(this, arguments);
      if (/\/cart\/(add|change|update|clear)\.js/.test(url)) {
        result.then(function (response) {
          if (!response.ok) return;
          /* The user is ahead of the server — keep the optimistic state. */
          if (hasPendingQuantity() || quantityFlushing) return;
          /* The background plan sync fires one /cart/change.js per line;
             syncCart renders once itself when it finishes. */
          if (syncing) return;
          /* PRICE AUTHORITY: mutation payloads (add/change/update/clear)
             are NEVER rendered. Shopify returns them with prices that can
             sit in a different market context than the session (observed:
             /cart/change.js allocation compare_at $3.75 while /cart.js
             says $4.24 for the same line) — every direct render of such a
             payload makes the totals dip and bounce back ~a second later.
             All rendering flows from a /cart.js refetch instead. */
          refreshCartState();
        }).catch(function () {});
      }
      return result;
    };
  })();

  function onCartMutated() {
    /* Give the in-flight cart request time to land first. */
    setTimeout(function () {
      refreshCartState();
    }, 500);
    scheduleSync();
  }

  /* ---------- Optimistic quantity engine (product card steppers) ----------
     Every +/- click renders instantly from a local optimistic cart model
     (visualizer, value summary, drawer header, card inputs), while the
     server writes are coalesced into a single /cart/update.js request.
     Rapid clicks therefore never fire parallel requests, never get dropped,
     and never freeze the UI. */

  var pendingQuantity = {};   /* variantId -> absolute target quantity */
  var quantityFlushTimer = null;
  var quantityFlushing = false;
  var QUANTITY_FLUSH_DELAY = 400;

  function hasPendingQuantity() {
    return Object.keys(pendingQuantity).length > 0;
  }

  /* While the user is actively working the DRAWER steppers, nothing is
     allowed to replace drawer HTML (an innerHTML swap destroys the button
     under the cursor and every following click lands in a dead DOM node).
     Interaction with PRODUCT CARD steppers does not block drawer re-renders:
     swapping the drawer can't drop a click aimed at a card. */
  var lastInteractionAt = 0;
  var lastDrawerInteractionAt = 0;
  var INTERACTION_WINDOW = 1500;

  function markInteraction(inDrawer) {
    lastInteractionAt = Date.now();
    if (inDrawer) lastDrawerInteractionAt = lastInteractionAt;
  }

  function isInteractingWithDrawer() {
    return Date.now() - lastDrawerInteractionAt < INTERACTION_WINDOW;
  }

  /* Last confirmed server cart — the base every optimistic model builds on. */
  var serverCart = null;

  function moneyTextToCents(text) {
    var value = parseFloat(String(text || '').replace(/[^0-9.]/g, ''));
    return isNaN(value) ? 0 : Math.round(value * 100);
  }

  /* ---------- Session-contextual price cache ----------
     Liquid renders card prices in the market context of the PAGE render
     (which can be CDN-cached in another country's context), while the cart
     always prices in the context of the buyer's session — with Shopify
     Markets these can differ (e.g. US $3.75 vs International $4.24 for the
     same variant). /products/{handle}.js is priced in the same session
     context as the cart, so it is the source of truth for optimistic math
     on items the server cart doesn't know yet. */
  var contextualPrices = {};
  var contextualPrimed = false;

  function cardElFor(node) {
    return node.closest('.gr-products-grid__card') || node.closest('.gr-card-product-new');
  }

  /* Warm the browser cache with the width-100 drawer images of every
     product card, so the very first optimistic add paints its image
     instantly instead of waiting for a CDN fetch. */
  var drawerImagesPrimed = false;
  function primeDrawerImages() {
    if (drawerImagesPrimed) return;
    drawerImagesPrimed = true;
    document.querySelectorAll('[data-cart-image]').forEach(function (card) {
      var url = card.getAttribute('data-cart-image');
      if (url) new Image().src = url;
    });
  }

  function primeContextualPrices() {
    if (contextualPrimed) return;
    contextualPrimed = true;
    var handles = {};
    document.querySelectorAll('[data-card-variant-id]').forEach(function (input) {
      var card = cardElFor(input);
      var link = card && card.querySelector('a[href*="/products/"]');
      var match = link && link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (match) handles[match[1]] = true;
    });
    var list = Object.keys(handles);
    if (!list.length) return;
    Promise.all(list.map(function (handle) {
      return fetch(ROOT + '/products/' + handle + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function () { return null; });
    })).then(function (products) {
      var loaded = false;
      products.forEach(function (product) {
        if (!product) return;
        (product.variants || []).forEach(function (variant) {
          contextualPrices[String(variant.id)] = variant.price; /* cents */
          loaded = true;
        });
      });
      if (loaded) reconcileCardPrices();
    });
  }

  /* Rewrite the card price data-attributes (and visible text) from the
     session-contextual prices, so every optimistic calculation — and what
     the customer sees — matches what the cart will actually charge. */
  function reconcileCardPrices() {
    var discount = getDiscountPercent();
    document.querySelectorAll('[data-card-price]').forEach(function (el) {
      var card = cardElFor(el);
      var input = card && card.querySelector('[data-card-variant-id]');
      var cents = input && contextualPrices[input.getAttribute('data-card-variant-id')];
      if (!cents) return;
      var onetime = formatMoney(cents);
      if (el.dataset.onetimePrice !== onetime) el.dataset.onetimePrice = onetime;
      if (el.dataset.subPrice) {
        var sub = formatMoney(Math.round(cents * (100 - discount) / 100));
        if (el.dataset.subPrice !== sub) el.dataset.subPrice = sub;
      }
    });
    updateCardPrices(getMode());
    if (hasPendingQuantity()) scheduleOptimisticRender();
  }

  /* An item added purely optimistically (not in the server cart yet):
     synthesize enough fields for the visualizer and value summary from
     the session price cache, falling back to the product card's DOM. */
  /* The base a variant id maps to on its card — mirrors what /cart.js will
     report as variant_title, so the optimistic drawer row and the server
     render can never disagree. */
  function variantTitleFor(card, variantId) {
    if (!card) return '';
    var vid = String(variantId);
    if (card.getAttribute('data-base-oats-id') === vid) return card.getAttribute('data-base-oats-title') || tr('baseOats', 'Rolled oats');
    if (card.getAttribute('data-base-barley-id') === vid) return card.getAttribute('data-base-barley-title') || tr('baseBarley', 'Black barley');
    return '';
  }

  /* Net weight of one bottle of this variant, from the card's per-base
     attributes (data-base-oats-net / data-base-barley-net, written from the
     variant's nutrition_facts metafield). */
  function netWeightFor(variantId) {
    var card = cardForVariant(variantId);
    if (card) {
      var vid = String(variantId);
      var attr = card.getAttribute('data-base-oats-id') === vid ? 'data-base-oats-net'
        : card.getAttribute('data-base-barley-id') === vid ? 'data-base-barley-net' : null;
      var net = attr && parseFloat(card.getAttribute(attr));
      if (net > 0) return net;
    }
    return POUCH_WEIGHT_G;
  }

  function stubItemFromCard(variantId, quantity) {
    var card = cardForVariant(variantId);
    var gridItem = card && card.closest('[data-product-type]');
    var img = card && card.querySelector('img');
    var priceEl = card && card.querySelector('[data-card-price]');
    var cents = contextualPrices[String(variantId)] ||
      (priceEl ? moneyTextToCents(priceEl.dataset.onetimePrice) : 0);
    /* data-cart-image is the exact width-100 URL the Liquid drawer render
       uses (and it is preloaded at idle), so the optimistic row and the
       later server HTML show the same, already-cached image — no visible
       reload when the canonical render lands. */
    var cartImage = card && card.getAttribute('data-cart-image');
    return {
      _gr_stub: true,
      variant_id: parseInt(variantId, 10) || variantId,
      quantity: quantity,
      product_title: (card && card.getAttribute('data-cart-title')) || '',
      variant_title: variantTitleFor(card, variantId),
      product_type: (card && card.getAttribute('data-cart-type')) ||
        (gridItem ? gridItem.getAttribute('data-product-type') : COMPATIBLE_TYPES[0]),
      image: cartImage || (img ? (img.currentSrc || img.src) : null),
      price: cents,
      original_price: cents
    };
  }

  function optimisticCart() {
    var base = serverCart || window.cart || { items: [] };
    var items = (base.items || []).map(function (item) {
      return Object.assign({}, item);
    });
    Object.keys(pendingQuantity).forEach(function (vid) {
      var target = pendingQuantity[vid];
      var lines = items.filter(function (item) {
        return String(item.variant_id) === String(vid);
      });
      if (lines.length) {
        lines[0].quantity = target;
        for (var i = 1; i < lines.length; i++) lines[i].quantity = 0;
      } else if (target > 0) {
        /* /cart.js lists newest first — a brand-new item goes to the front
           so it lands in the rightmost visualizer slot. */
        items.unshift(stubItemFromCard(vid, target));
      }
    });
    items = items.filter(function (item) {
      return item.quantity > 0;
    });
    var count = items.reduce(function (sum, item) {
      return sum + item.quantity;
    }, 0);
    return Object.assign({}, base, { items: items, item_count: count });
  }

  function applyDrawerSections(cartData) {
    if (!cartData || !cartData.sections) return;
    var parser = new DOMParser();
    if (cartData.sections['cart-drawer']) {
      var doc = parser.parseFromString(cartData.sections['cart-drawer'], 'text/html');
      var drawerSection = doc.querySelector('.shopify-section cart-drawer');
      var opener = doc.querySelector('.shopify-section #CartMobOpener');
      var pageDrawer = document.querySelector('cart-drawer');
      var pageOpener = document.querySelector('#CartMobOpener');
      if (pageDrawer && drawerSection) {
        pageDrawer.innerHTML = drawerSection.innerHTML;
        /* Empty state is server truth only: mirror the class from the
           freshly rendered fragment, never from a transient local guess. */
        pageDrawer.classList.toggle('is-empty', drawerSection.classList.contains('is-empty'));
      }
      if (pageOpener && opener) pageOpener.innerHTML = opener.innerHTML;
    }
    if (cartData.sections['cart-icon-bubble']) {
      var bubbleDoc = parser.parseFromString(cartData.sections['cart-icon-bubble'], 'text/html');
      var bubbleSection = bubbleDoc.querySelector('.shopify-section');
      var bubble = document.querySelector('#cart-icon-bubble');
      if (bubble && bubbleSection) bubble.innerHTML = bubbleSection.innerHTML;
    }
  }

  /* Coalesce optimistic renders to one per frame: the input number is
     already updated synchronously on click, so a click burst never piles
     up full-page render work on the main thread. */
  var optimisticRaf = null;
  function scheduleOptimisticRender() {
    if (optimisticRaf) return;
    optimisticRaf = requestAnimationFrame(function () {
      optimisticRaf = null;
      renderCartState(optimisticCart());
    });
  }

  function queueQuantityUpdate(variantId, quantity) {
    pendingQuantity[variantId] = Math.max(0, parseInt(quantity, 10) || 0);
    /* Invalidate any /cart.js refetch already in flight: its response no
       longer reflects this click and would repaint the pre-click state for
       ~a flush cycle before the queue corrects it. */
    renderToken++;
    scheduleOptimisticRender();
    clearTimeout(quantityFlushTimer);
    quantityFlushTimer = setTimeout(flushQuantityQueue, QUANTITY_FLUSH_DELAY);
  }

  window.grQueueCartUpdate = queueQuantityUpdate;
  window.grScheduleDrawerRerender = function () { scheduleDrawerRerender(); };

  /* Instant add for surfaces without a stepper (home preview row, quick
     view): +1 through the SAME optimistic queue the card steppers use —
     the drawer row paints this frame, the server write is coalesced.
     Optionally opens the cart drawer so the add is visibly confirmed. */
  window.grOptimisticAdd = function (variantId, openDrawer) {
    if (!variantId) return;
    var current = 0;
    (optimisticCart().items || []).forEach(function (item) {
      if (String(item.variant_id) === String(variantId)) current += item.quantity;
    });
    queueQuantityUpdate(variantId, current + 1);
    if (openDrawer) {
      var drawer = document.querySelector('cart-drawer');
      if (drawer) {
        if (typeof drawer.open === 'function') {
          drawer.open();
        } else {
          drawer.classList.add('active');
          document.body.classList.add('overflow-hidden');
        }
      }
    }
  };

  /* Base switch (gr-base-choice.js): the card's data-card-variant-id now
     points at the other variant. Re-mirror stepper values / in-cart state
     from the local cart picture and re-derive prices — synchronously, no
     network. The quantity queue stays untouched: pending updates are keyed
     by variant id, so a mid-burst switch cannot cross the two bases. */
  document.addEventListener('gr:base-changed', function () {
    syncQuantityControls(optimisticCart());
    reconcileCardPrices();
  });

  /* Mirror the paired inputs (product card <-> drawer) synchronously so
     both steppers show the new number in the same tick as the click. */
  function mirrorQuantityInputs(variantId, value) {
    document.querySelectorAll(
      '[data-card-variant-id="' + variantId + '"], [data-quantity-variant-id="' + variantId + '"]'
    ).forEach(function (el) {
      if (String(el.value) !== String(value)) el.value = value;
    });
  }

  /* ---------- True optimistic steppers ----------
     A single document-level CAPTURE click listener owns every +/- button
     (product cards AND drawer). It runs before any theme script can react:
     stopImmediatePropagation() kills the legacy per-click handlers
     (grQuantityInput, Dawn's CartItems), the input number is updated
     synchronously as the very first step, then the value goes into the
     debounced /cart/update.js queue. Nothing here ever waits for, or is
     blocked by, the network. */
  document.addEventListener('click', function (event) {
    var btn = event.target.closest('.gr-quantity__button');
    if (!btn) return;
    var wrap = btn.closest('gr-quantity-selector');
    var input = wrap && wrap.querySelector('input');
    if (!input) return;
    var vid = input.getAttribute('data-card-variant-id') || input.getAttribute('data-quantity-variant-id');
    if (!vid) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    markInteraction(!!btn.closest('cart-drawer, .gr-page-with-cart__cart-wrap'));

    var isPlus = btn.name === 'plus' || btn.classList.contains('gr-quantity__button--plus');
    var current = parseInt(input.value, 10) || 0;
    var next = isPlus ? current + 1 : Math.max(0, current - 1);
    if (next === current) return;

    /* 1. Synchronous DOM update — before anything else. */
    input.value = next;
    mirrorQuantityInputs(vid, next);
    syncStepperButtons(input, next);
    /* 2. Optimistic render (visualizer, summary, header) + debounced queue. */
    queueQuantityUpdate(vid, next);
  }, true);

  /* Drawer steppers: intercept the change event in the CAPTURE phase, before
     Dawn's CartItems debounced handler fires its per-click AJAX (which locks
     the list with disabled overlays and re-renders the drawer on every
     click). stopPropagation() keeps that legacy path from ever running —
     drawer clicks flow through the same optimistic queue as product cards. */
  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches('input[data-quantity-variant-id]')) return;
    if (!input.closest('cart-drawer, cart-drawer-items, cart-items, .gr-page-with-cart__cart-wrap')) return;
    event.stopPropagation();
    markInteraction(true);
    queueQuantityUpdate(input.getAttribute('data-quantity-variant-id'), input.value);
  }, true);

  var quantityFlushRetries = 0;
  var QUANTITY_FLUSH_MAX_RETRIES = 4;

  function flushQuantityQueue() {
    if (quantityFlushing || !hasPendingQuantity()) return;
    /* Mutual exclusion with the plan sync: syncCart sends /cart/change.js
       with absolute quantities read from an earlier /cart.js — if this
       flush's /cart/update.js interleaves with one of those, whichever
       request lands last silently wins with a stale number (the "added 2,
       snapped back to 1" bug). Wait the sync out; it likewise defers to us
       while the queue is busy. */
    if (syncing) {
      clearTimeout(quantityFlushTimer);
      quantityFlushTimer = setTimeout(flushQuantityQueue, 150);
      return;
    }
    var updates = pendingQuantity;
    pendingQuantity = {};
    quantityFlushing = true;
    var failed = false;
    /* Data-only write: NO `sections` in the payload, so Shopify never sends
       replacement HTML and no innerHTML swap can ever happen from this path.
       The response payload is deliberately NOT rendered (mutation payloads
       can carry prices in a different market context than the session);
       the canonical /cart.js refetch below is the only source of truth. */
    cartPost(ROOT + '/cart/update.js', { updates: updates }).then(function () {
      quantityFlushRetries = 0;
      /* Patch the flushed QUANTITIES straight into serverCart so it is
         never stale between this success and the next /cart.js refetch —
         any renderer that reads it (base switch, optimistic frames) sees
         the numbers the server just accepted. Prices are left untouched
         (PRICE AUTHORITY stays with /cart.js); brand-new lines come in as
         the same stubs the optimistic drawer already painted. */
      if (serverCart && serverCart.items) {
        var patched = serverCart.items.map(function (item) {
          return Object.assign({}, item);
        });
        Object.keys(updates).forEach(function (vid) {
          var target = updates[vid];
          var found = false;
          patched.forEach(function (item) {
            if (String(item.variant_id) === String(vid)) {
              item.quantity = found ? 0 : target;
              found = true;
            }
          });
          if (!found && target > 0) patched.unshift(stubItemFromCard(vid, target));
        });
        patched = patched.filter(function (item) { return item.quantity > 0; });
        serverCart = Object.assign({}, serverCart, {
          items: patched,
          item_count: patched.reduce(function (s, i) { return s + i.quantity; }, 0)
        });
      }
      scheduleSync();
      scheduleDrawerRerender();
    }).catch(function (e) {
      /* Shopify throttles cart endpoints (429) under rapid clicking.
         Dropping the batch here repaints stale server numbers and the
         steppers read as frozen — instead, RE-QUEUE the failed targets
         (never overriding a newer click) and retry with backoff. */
      failed = true;
      console.error('Cart update failed, retrying:', e);
      Object.keys(updates).forEach(function (vid) {
        if (!(vid in pendingQuantity)) pendingQuantity[vid] = updates[vid];
      });
    }).finally(function () {
      quantityFlushing = false;
      if (failed) {
        quantityFlushRetries++;
        if (quantityFlushRetries > QUANTITY_FLUSH_MAX_RETRIES) {
          /* Give up: drop the queue and show server truth rather than
             leaving the UI permanently out of sync. */
          quantityFlushRetries = 0;
          pendingQuantity = {};
          refreshCartState();
          return;
        }
        clearTimeout(quantityFlushTimer);
        quantityFlushTimer = setTimeout(flushQuantityQueue, 700 * quantityFlushRetries);
        return;
      }
      if (hasPendingQuantity()) {
        flushQuantityQueue();
      } else {
        /* Render from the canonical /cart.js state (must run after the
           quantityFlushing flag is cleared, or the refetch guard skips it). */
        refreshCartState();
      }
    });
  }

  /* Structural drawer re-render (new/removed line items) via a read-only
     Section Rendering GET — never a cart mutation. Runs as soon as the
     server data is written; it is deferred only while the user is actively
     clicking DRAWER steppers (a swap there would drop their next click).
     The existing optimistic DOM stays fully visible while the fetch is in
     flight — the swap happens only when the response has arrived. */
  var drawerRerenderTimer = null;

  function scheduleDrawerRerender(delayMs) {
    clearTimeout(drawerRerenderTimer);
    drawerRerenderTimer = setTimeout(function () {
      /* `syncing` gate: sections rendered mid plan-application would show a
         half-synced cart — wait for syncCart to finish (it reschedules). */
      if (hasPendingQuantity() || quantityFlushing || syncing || isInteractingWithDrawer()) {
        scheduleDrawerRerender(400);
        return;
      }
      /* ATOMIC swap: fetch the section HTML and the canonical /cart.js IN
         PARALLEL, then apply both in one task. The old pipeline rendered
         the last-known serverCart between the swap and the refetch — after
         a remove-last-unit flush that cart was STALE (refreshes are gated
         while the flush is in flight), so the deleted row flashed back for
         one network roundtrip before vanishing again. With one atomic
         apply+render from fresh data there is no stale window at all, and
         Liquid's cent-rounding still never gets painted. */
      Promise.all([
        fetch(window.location.pathname + '?sections=cart-drawer,cart-icon-bubble')
          .then(function (r) { return r.json(); }),
        fetchCart()
      ])
        .then(function (results) {
          if (hasPendingQuantity() || quantityFlushing || syncing || isInteractingWithDrawer()) {
            scheduleDrawerRerender(400);
            return;
          }
          applyDrawerSections({ sections: results[0] });
          serverCart = results[1];
          renderToken++; /* nothing older may render over this */
          renderCartState(results[1]);
        })
        .catch(function () {});
    }, typeof delayMs === 'number' ? delayMs : 100);
  }

  /* ---------- "Start Easy" pre-built bundles ---------- */

  function ensureBundleModal() {
    var modal = document.querySelector('.gr-bundle-modal');
    if (modal) return modal;
    modal = document.createElement('div');
    modal.className = 'gr-bundle-modal';
    modal.innerHTML =
      '<div class="gr-bundle-modal__overlay" data-bundle-cancel></div>' +
      '<div class="gr-bundle-modal__inner" role="dialog" aria-modal="true" aria-label="' + escapeHtml(tr('bundleReplaceDialog', 'Replace cart confirmation')) + '">' +
        '<h3 class="gr-bundle-modal__title">' + escapeHtml(tr('bundleReplaceTitle', 'Replace your current box?')) + '</h3>' +
        '<p class="gr-bundle-modal__text">' + escapeHtml(tr('bundleReplaceText', 'Do you want to clear your current selection and replace it with this pre-built bundle?')) + '</p>' +
        '<div class="gr-bundle-modal__buttons">' +
          '<button type="button" class="gr-button gr-button--solid gr-bundle-modal__button" data-bundle-cancel>' + escapeHtml(tr('bundleCancel', 'Cancel')) + '</button>' +
          '<button type="button" class="gr-button gr-button--accent gr-bundle-modal__button" data-bundle-replace>' + escapeHtml(tr('bundleReplace', 'Replace cart')) + '</button>' +
        '</div>' +
      '</div>';
    document.body.appendChild(modal);
    return modal;
  }

  var pendingBundle = null;

  function showBundleSuccess(button) {
    if (!button) return;
    var label = button.querySelector('[data-bundle-label], .gr-bundle-card__add-label, .gr-starter-boxes__add-label');
    var original = label ? label.textContent : null;
    if (label) label.textContent = tr('bundleAdded', 'Added!');
    button.classList.add('is-added');
    setTimeout(function () {
      if (label && original !== null) label.textContent = original;
      button.classList.remove('is-added');
    }, 2000);
  }

  function addBundle(items, button) {
    var mode = getMode();
    var planId = getPlanId();
    var payload = items.map(function (item) {
      var line = { id: item.id, quantity: item.quantity };
      if (mode === SUB && planId) line.selling_plan = planId;
      return line;
    });
    var drawer = document.querySelector('cart-drawer');
    var body = { items: payload, sections_url: window.location.pathname };
    if (drawer && drawer.getSectionsToRender) {
      body.sections = drawer.getSectionsToRender().map(function (s) { return s.id; });
    }
    return cartPost(ROOT + '/cart/add.js', body).then(function (state) {
      if (state && state.status) {
        console.error('Bundle add failed:', state);
        return;
      }
      showBundleSuccess(button);
      /* Re-render + open the drawer so the user sees their new box. */
      if (drawer && state && state.sections) drawer.renderContents(state);
    }).finally(function () {
      if (button) button.classList.remove('is-loading');
    });
  }

  function runBundle(items, button, clearFirst) {
    if (button) button.classList.add('is-loading');
    var pre = clearFirst ? cartPost(ROOT + '/cart/clear.js', {}) : Promise.resolve();
    return pre.then(function () {
      return addBundle(items, button);
    }).catch(function (e) {
      console.error('Bundle failed:', e);
      if (button) button.classList.remove('is-loading');
    });
  }

  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-bundle-cancel]')) {
      var cancelModal = document.querySelector('.gr-bundle-modal');
      if (cancelModal) cancelModal.classList.remove('is-open');
      pendingBundle = null;
      return;
    }

    if (event.target.closest('[data-bundle-replace]')) {
      var replaceModal = document.querySelector('.gr-bundle-modal');
      if (replaceModal) replaceModal.classList.remove('is-open');
      if (pendingBundle) runBundle(pendingBundle.items, pendingBundle.button, true);
      pendingBundle = null;
      return;
    }

    var button = event.target.closest('[data-bundle-items]');
    if (!button || button.classList.contains('is-loading')) return;

    var items;
    try {
      items = JSON.parse(button.dataset.bundleItems);
    } catch (e) {
      console.error('Bundle: invalid items JSON', e);
      return;
    }
    if (!items.length) return;

    fetchCart().then(function (cartData) {
      if (!cartData.item_count) {
        runBundle(items, button, false);
      } else {
        pendingBundle = { items: items, button: button };
        ensureBundleModal().classList.add('is-open');
      }
    });
  });

  /* ---------- Mobile sticky Box Builder ----------
     On phones/tablets the builder card pins below the sticky header once the
     page scrolls past it, so the pouch count stays visible while browsing
     the grid. A spacer holds the card's slot in the layout (no jump), and
     the top offset tracks the theme header as it slides in/out. */
  function initStickyBoxBuilder() {
    var bb = document.querySelector('[data-box-builder]');
    if (!bb) return;
    var mql = window.matchMedia('(max-width: 1279px)');
    var spacer = document.createElement('div');
    spacer.setAttribute('aria-hidden', 'true');
    spacer.style.display = 'none';
    bb.parentNode.insertBefore(spacer, bb);
    var stuck = false;
    var ticking = false;
    var GAP = 8;

    function headerBottom() {
      var header = document.querySelector('.shopify-section-header-sticky');
      if (!header) return 0;
      var bottom = header.getBoundingClientRect().bottom;
      return bottom > 0 ? bottom : 0;
    }

    function stick(offset) {
      stuck = true;
      /* The spacer must cover the card's full layout footprint including
         its bottom margin (which the --stuck class zeroes), or the grid
         below shifts up at the stick boundary. */
      var margin = parseFloat(getComputedStyle(bb).marginBottom) || 0;
      spacer.style.height = (bb.offsetHeight + margin) + 'px';
      spacer.style.display = 'block';
      bb.classList.add('gr-box-builder--stuck');
      bb.style.top = offset + GAP + 'px';
    }

    function unstick() {
      if (!stuck) return;
      stuck = false;
      spacer.style.display = 'none';
      bb.classList.remove('gr-box-builder--stuck');
      bb.style.top = '';
    }

    function update() {
      ticking = false;
      if (!mql.matches) {
        unstick();
        return;
      }
      var offset = headerBottom();
      /* The spacer marks the card's natural position once it is stuck. */
      var anchorTop = (stuck ? spacer : bb).getBoundingClientRect().top;
      if (!stuck && anchorTop < offset + GAP) {
        stick(offset);
      } else if (stuck) {
        if (anchorTop >= offset + GAP) {
          unstick();
        } else {
          var top = offset + GAP + 'px';
          if (bb.style.top !== top) bb.style.top = top;
        }
      }
    }

    function requestUpdate() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    }

    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    /* Dawn's sticky header animates its `top` for 0.15s AFTER the last
       scroll event — re-measure when that transition settles so the header
       never ends up parked over the stuck card. */
    var headerSection = document.querySelector('.section-header');
    if (headerSection) headerSection.addEventListener('transitionend', requestUpdate);
    requestUpdate();
  }

  /* ---------- Wiring ---------- */

  document.addEventListener('change', function (event) {
    var target = event.target;

    if (target.matches('input[name="gr-purchase-mode"]')) {
      setMode(target.value);
      updateUI();
      syncCart(target.value, true);
      return;
    }

    if (target.matches('input[name="gr-deliver-every"]')) {
      setStoredCadence(target.value);
      /* Picking a frequency implies subscribing. */
      setMode(SUB);
      updateUI();
      syncCart(SUB, true);
      return;
    }

    if (target.matches('.gr-pdp-mode input[data-mode-value]')) {
      var mode = target.dataset.modeValue === 'subscription' ? SUB : ONE;
      setMode(mode);
      updateUI();
      syncCart(mode, true);
    }
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest('.gr-purchase-checkout');
    if (!button) return;
    event.preventDefault();

    var popup = document.querySelector('.gr-drawer-popup');

    if (button.dataset.setMode) {
      var mode = button.dataset.setMode === SUB ? SUB : ONE;
      setMode(mode);
      updateUI();
      if (popup) popup.classList.remove('gr-open');
      proceedToCheckout(button);
      return;
    }

    var upsellShown = false;
    try {
      upsellShown = !!sessionStorage.getItem(UPSELL_KEY);
    } catch (e) { /* noop */ }

    if (getMode() === ONE && popup && !upsellShown) {
      try {
        sessionStorage.setItem(UPSELL_KEY, '1');
      } catch (e) { /* noop */ }
      popup.classList.add('gr-open');
      return;
    }

    proceedToCheckout(button);
  });

  /* Re-apply UI state after the drawer is re-rendered via innerHTML. */
  var uiRaf = null;
  var observer = new MutationObserver(function (mutations) {
    var relevant = mutations.some(function (m) {
      return Array.prototype.some.call(m.addedNodes, function (node) {
        return node.nodeType === 1 && (
          node.matches && (node.matches('.gr-purchase-mode') || node.querySelector && node.querySelector('.gr-purchase-mode'))
        );
      });
    });
    if (!relevant) return;
    if (uiRaf) cancelAnimationFrame(uiRaf);
    uiRaf = requestAnimationFrame(updateUI);
  });

  function init() {
    updateUI();
    observer.observe(document.body, { childList: true, subtree: true });

    /* Drawer quantity inputs go through cart.js (/cart/change.js) without
       publishing events — watch input changes as a safety net. Product card
       steppers are handled by the optimistic queue (grQueueCartUpdate). */
    document.addEventListener('change', function (event) {
      if (event.target.matches('[data-card-variant-id]')) return;
      if (event.target.matches('[data-quantity-variant-id]')) {
        onCartMutated();
      }
    });

    /* Form-based adds (product-form) publish cartUpdate. */
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
        onCartMutated();
      });
    }

    /* Initial reconciliation from the live cart. */
    refreshCartState();

    initStickyBoxBuilder();

    /* Hydrate session-contextual prices + drawer images for optimistic
       rendering (idle work). */
    var primeAll = function () {
      primeContextualPrices();
      primeDrawerImages();
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(primeAll, { timeout: 2000 });
    } else {
      setTimeout(primeAll, 400);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
